import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';

// Pay one player's training share from their Abo or Gutschein (CHF amount).
export const TrainingPrepaidPay = ({ booking, API, L, pp, onPaid }) => {
  const ids = booking.client_ids || (booking.user_id ? [booking.user_id] : []);
  const names = booking.client_names || (booking.user_name ? [booking.user_name] : []);
  const covered = new Set([...(booking.paid_via_subscriptions || []), ...(booking.paid_via_vouchers || [])].map(e => e.client_id));
  const open = ids.map((id, i) => ({ id, name: names[i] || '' })).filter(c => !covered.has(c.id));
  const [clientId, setClientId] = useState(open.length === 1 ? open[0].id : '');
  const [options, setOptions] = useState(null);
  const [pick, setPick] = useState('');
  const [amount, setAmount] = useState(pp ? pp.toFixed(2) : '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!clientId) { setOptions(null); return; }
    setPick('');
    Promise.all([
      API.get(`/subscriptions?user_id=${clientId}`).catch(() => ({ data: [] })),
      API.get(`/vouchers/user/${clientId}`).catch(() => ({ data: [] })),
    ]).then(([s, v]) => setOptions([
      ...(s.data || []).filter(x => (x.balance_remaining || 0) > 0.01 && !['cancelled', 'expired', 'refunded'].includes(x.status)).map(x => ({ key: `s:${x.id}`, label: `Abo ${x.type || ''} · ${x.used_lessons || 0}/${x.total_lessons || 0} · ${(x.balance_remaining || 0).toFixed(2)} CHF`, bal: x.balance_remaining })),
      ...(v.data || []).map(x => ({ key: `v:${x.id}`, label: `${L('Voucher', 'Gutschein')} ${x.code || ''} · ${(x.remaining_balance || 0).toFixed(2)} CHF`, bal: x.remaining_balance })),
    ]));
  }, [clientId, API, L]);

  if (open.length === 0) return null;
  const sel = (options || []).find(o => o.key === pick);
  const amt = parseFloat(amount) || 0;
  const tooMuch = sel && amt > (sel.bal || 0) + 0.005;

  const submit = async () => {
    setBusy(true);
    try {
      const [kind, id] = pick.split(':');
      const { data } = await API.post(`/trainer/lessons/${booking.id}/pay-prepaid`, {
        client_id: clientId, amount: amt, ...(kind === 's' ? { subscription_id: id } : { voucher_id: id }),
      });
      toast.success(L(`CHF ${amt.toFixed(2)} deducted`, `CHF ${amt.toFixed(2)} abgezogen`));
      onPaid(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2" data-testid="training-prepaid-panel">
      <p className="text-xs font-semibold text-violet-900">{L('Pay with Abo / Voucher', 'Mit Abo / Gutschein bezahlen')}</p>
      <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full h-9 rounded-md border border-violet-200 bg-white px-2 text-sm" data-testid="prepaid-client-select">
        <option value="">{L('Choose player…', 'Spieler wählen…')}</option>
        {open.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {clientId && options && options.length === 0 && (
        <p className="text-xs text-slate-500" data-testid="prepaid-none">{L('No active Abo or voucher for this player.', 'Kein aktives Abo oder Gutschein für diesen Spieler.')}</p>
      )}
      {options && options.length > 0 && (
        <select value={pick} onChange={(e) => setPick(e.target.value)} className="w-full h-9 rounded-md border border-violet-200 bg-white px-2 text-sm" data-testid="prepaid-source-select">
          <option value="">{L('Choose Abo / voucher…', 'Abo / Gutschein wählen…')}</option>
          {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      )}
      {pick && (
        <div className="flex gap-2 items-center">
          <Input type="number" step="0.05" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-white h-9" data-testid="prepaid-amount-input" />
          <span className="text-sm text-slate-600">CHF</span>
          <Button size="sm" onClick={submit} disabled={busy || amt <= 0 || tooMuch} className="h-9 bg-violet-600 hover:bg-violet-700 text-white font-bold" data-testid="prepaid-submit">
            {busy ? '…' : L('Deduct', 'Abziehen')}
          </Button>
        </div>
      )}
      {tooMuch && <p className="text-xs text-red-600" data-testid="prepaid-too-much">{L('More than the remaining balance', 'Mehr als das Restguthaben')}</p>}
    </div>
  );
};
